import { memo, useState, useEffect } from "react";
import styled from "styled-components";
import { ClipLoaderCommon, StyledButton } from "@components";
import {
    fetchPoints,
    selectAccount,
    selectPoints,
    useAppDispatch,
    useAppSelector,
    useIsLoggedIn,
} from "@redux";
import { breakpoint, theme, useQuoteActions } from "@utilities";
import { useCheckout } from "../CheckoutContext";
import { useSearchParams, useNavigate, useParams } from "react-router-dom";
import { ErrorToast } from "src/Components/toasts/ErrorToast";
import { selectQuotes, loadSharedQuotes } from "src/Redux/Slices/allQuotes";
import { PATH_QUOTES } from "@routes";
import { calcTotalUsablePoints, formatMaxOneDecimal } from "../pointsHelpers";
import { CART_TEASER_MAX_PERCENT, cartTeaserCopy } from "../signupOfferConfig";
import { isDisplayablePrice } from "src/Utilities/priceHelper";

interface IProps {
    btnTitle?: string;
    onBtnClick?: () => void;
    page?: string;
    paymentOption?: string;
    disableNext?: boolean;
    isSubmitting?: boolean;
    totalPrice?: number;
    disabled?: boolean;
    onSaveToQuote?: () => void;
    onAddToExistingQuote?: () => void;
    isSavingToQuote?: boolean;
}

export const CheckoutItemsActions = memo((props: IProps) => {
    const { btnTitle, isSubmitting, onBtnClick, disableNext, disabled } = props;
    const [showHelp, setShowHelp] = useState(false);
    const { account, metadata } = useAppSelector(selectAccount);
    const { data: pointsData } = useAppSelector(selectPoints);
    const { loadingStates, handleAcceptQuote, handleRejectQuote } = useQuoteActions({
        onSuccess: () => navigate(PATH_QUOTES),
    });
    const isLoggedIn = useIsLoggedIn();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { quoteId } = useParams<{ quoteId: string }>();
    const [searchParams] = useSearchParams();
    const isPending = searchParams.get("isPending") === "true";

    // Get quoteShareId from sharedQuotes data by matching the current quoteId
    const { sharedQuotes } = useAppSelector(selectQuotes);
    const currentSharedQuote = sharedQuotes.find((quote) => quote.id.toString() === quoteId);
    const quoteShareId = currentSharedQuote?.quote_share_id;

    // Load shared quotes when component mounts if isPending
    useEffect(() => {
        if (isPending && sharedQuotes.length === 0) {
            dispatch(loadSharedQuotes());
        }
    }, [isPending, dispatch, sharedQuotes.length]);

    const {
        activeDiscount,
        orderDiscount,
        isOrderDiscountLoading,
        discountedPrice,
        originalPrice,
        isUpgradingPoints,
        pointsAvailable,
    } = useCheckout();

    // Treat an in-flight points upgrade exactly like a discount recalculation:
    // both mean the figures below are about to change, so the summary should show
    // its loading state rather than totals that look settled.
    const isTotalsPending = isOrderDiscountLoading || isUpgradingPoints;

    // Totals are derived from the cart items, so until those land the arithmetic
    // runs on undefined and comes out NaN — which toFixed renders as the literal
    // "NaN". Treated as another kind of pending, so the figure is never shown
    // until it is a real number.
    const hasResolvedTotal = isDisplayablePrice(originalPrice);
    const isTotalUnavailable = isTotalsPending || !hasResolvedTotal;

    // Anyone not yet earning points is the one who could still be paying less, so
    // the teaser is not gated on being signed in: a guest now reaches the cart and
    // edits it before ever being asked for an account, and this is what tells them
    // what signing up is worth. Agents already see their real points and commission
    // figures, so the teaser would only clutter theirs.
    //
    // A guest is judged on being signed out alone. The account slice is persisted
    // and outlives the JWT, so a browser that was once signed in as an agent still
    // carries is_agent_integrated — which silently hid the teaser from the very
    // visitors it is meant for.
    const showEarnPotential = !isTotalUnavailable && (!isLoggedIn || !account?.is_agent_integrated);
    const potentialPrice = originalPrice * (1 - CART_TEASER_MAX_PERCENT / 100);

    const isCommissionAgent = metadata?.is_commission_agent;
    const isPointsAgent = metadata?.is_points_agent ?? false;
    const { loading: isPointsDataLoading } = useAppSelector(selectPoints);

    useEffect(() => {
        if (account?.is_agent_integrated && !pointsData) {
            dispatch(fetchPoints());
        }
    }, [account?.is_agent_integrated, dispatch, pointsData]);

    const commission =
        pointsData?.pointsMultiplier && pointsAvailable
            ? pointsAvailable / pointsData.pointsMultiplier
            : 0;

    const totalUsablePoints = calcTotalUsablePoints(pointsData?.availableBalance, pointsAvailable);

    const handleAccept = async () => {
        if (!quoteShareId) {
            ErrorToast({ Message: "Quote share ID not found" });
            return;
        }
        await handleAcceptQuote(quoteShareId);
    };

    const handleDecline = async () => {
        if (!quoteShareId) {
            ErrorToast({ Message: "Quote share ID not found" });
            return;
        }
        await handleRejectQuote(quoteShareId);
    };

    return (
        <CheckoutActionsStyled>
            {showHelp && (
                <div className="help-popup-overlay" onClick={() => setShowHelp(false)}>
                    <div className="help-popup-content" onClick={(e) => e.stopPropagation()}>
                        <p>
                            The final discount shown is based on the items in your cart that are
                            eligible for the offer. Some products may not qualify, while others
                            might have reduced discounts due to operator limitations. That’s why the
                            final discount may be lower than expected — it’s automatically adjusted
                            based on eligible items only.
                        </p>
                        <StyledButton
                            $borderRadius={theme.borderRadius.small}
                            color={theme.gradients.button}
                            onClick={() => setShowHelp(false)}
                        >
                            Got it
                        </StyledButton>
                    </div>
                </div>
            )}
            {isLoggedIn &&
                account?.is_agent_integrated &&
                account?.is_points_displayed &&
                isCommissionAgent &&
                !isTotalsPending &&
                commission > 0 && (
                    <MobileCommissionLabel>
                        You will earn ${formatMaxOneDecimal(commission)} Commission from this order
                    </MobileCommissionLabel>
                )}
            <div className="wrapper">
                <PriceBlockStyled>
                    <div className="price-label-wrapper">
                        <span className="price-label">Total Price</span>
                        {showEarnPotential && (
                            <EarnPotentialBadge>{cartTeaserCopy.badge}</EarnPotentialBadge>
                        )}
                        {isLoggedIn &&
                        account?.is_agent_integrated &&
                        account?.is_points_displayed &&
                        isPointsAgent ? (
                            <div className="price-discount-badge-wrapper">
                                {isTotalsPending ||
                                isPointsDataLoading ||
                                pointsAvailable === undefined ? (
                                    <div className="loading-spinner"></div>
                                ) : (
                                    <span className="price-discount-badge">
                                        {formatMaxOneDecimal(totalUsablePoints)} pts
                                    </span>
                                )}
                            </div>
                        ) : (
                            !account?.is_agent_integrated &&
                            (activeDiscount ?? 0) > 0 && (
                                <div className="price-discount-badge-wrapper">
                                    {isTotalsPending ? (
                                        <div className="loading-spinner"></div>
                                    ) : (
                                        (orderDiscount ?? 0) > 0 && (
                                            <>
                                                <span className="price-discount-badge">
                                                    {orderDiscount}% off
                                                </span>
                                                <QuestionMarkHelper>
                                                    <img
                                                        onClick={() => setShowHelp(true)}
                                                        src="/images/HelpIcon.svg"
                                                        alt="info"
                                                    />
                                                </QuestionMarkHelper>
                                            </>
                                        )
                                    )}
                                </div>
                            )
                        )}
                    </div>
                    {showEarnPotential && <PotentialNote>{cartTeaserCopy.subtitle}</PotentialNote>}
                    {isTotalUnavailable && (
                        <div className="price-value-wrapper">
                            <div className="loading-spinner"></div>
                        </div>
                    )}
                    {!isTotalUnavailable && (
                        <div className="price-value-wrapper">
                            {isLoggedIn && account?.is_agent_integrated ? (
                                <span className="price-discounted-value">
                                    ${originalPrice.toFixed(2)}
                                </span>
                            ) : (
                                <>
                                    {/* Show discounted price with strikethrough original price when discountedPrice exists and is less than originalPrice */}
                                    {discountedPrice &&
                                    originalPrice > discountedPrice &&
                                    (activeDiscount ?? 0) > 0 ? (
                                        <>
                                            <span className="price-discounted-value">
                                                ${discountedPrice.toFixed(2)}
                                            </span>
                                            <span className="price-original-value">
                                                ${originalPrice.toFixed(2)}
                                            </span>
                                        </>
                                    ) : showEarnPotential ? (
                                        <>
                                            <span className="price-potential-value">
                                                ${potentialPrice.toFixed(2)}
                                            </span>
                                            <span className="price-original-value">
                                                ${originalPrice.toFixed(2)}
                                            </span>
                                        </>
                                    ) : (
                                        <span className="price-discounted-value">
                                            ${originalPrice.toFixed(2)}
                                        </span>
                                    )}
                                </>
                            )}
                        </div>
                    )}
                </PriceBlockStyled>
                {isPending ? (
                    <PendingButtonsContainer>
                        <AcceptButton
                            $borderRadius={theme.borderRadius.small}
                            onClick={handleAccept}
                            disabled={!!loadingStates[quoteShareId!]}
                        >
                            {loadingStates[quoteShareId!] === "accept" ? (
                                <ClipLoaderCommon
                                    color={theme.colors.highlight}
                                    size={24}
                                    margin={150}
                                />
                            ) : (
                                "Accept"
                            )}
                        </AcceptButton>
                        <DeclineButton
                            $borderRadius={theme.borderRadius.small}
                            onClick={handleDecline}
                            disabled={!!loadingStates[quoteShareId!]}
                        >
                            {loadingStates[quoteShareId!] === "reject" ? (
                                <ClipLoaderCommon
                                    color={theme.colors.highlight}
                                    size={24}
                                    margin={150}
                                />
                            ) : (
                                "Decline"
                            )}
                        </DeclineButton>
                    </PendingButtonsContainer>
                ) : (
                    <>
                        <CheckoutButtonStyled
                            $borderRadius={theme.borderRadius.small}
                            color={theme.gradients.button}
                            onClick={onBtnClick}
                            disabled={
                                disableNext || isSubmitting || disabled || props.isSavingToQuote
                            }
                        >
                            {isSubmitting ? (
                                <ClipLoaderCommon
                                    color={theme.colors.highlight}
                                    size={24}
                                    margin={150}
                                />
                            ) : (
                                btnTitle
                            )}
                        </CheckoutButtonStyled>
                        {props.onSaveToQuote && (
                            <SaveToQuoteDropdown
                                onSaveNew={props.onSaveToQuote}
                                onAddToExisting={props.onAddToExistingQuote}
                                isSaving={props.isSavingToQuote}
                                isSubmitting={isSubmitting}
                            />
                        )}
                    </>
                )}
            </div>
            {isLoggedIn &&
                account?.is_agent_integrated &&
                account?.is_points_displayed &&
                isCommissionAgent &&
                !isTotalsPending &&
                commission > 0 && (
                    <DesktopCommissionLabel>
                        You will earn ${formatMaxOneDecimal(commission)} Commission from this order
                    </DesktopCommissionLabel>
                )}
        </CheckoutActionsStyled>
    );
});

const CheckoutActionsStyled = styled.div`
    background-color: #fff;
    border-radius: ${theme.borderRadius.medium};

    ${breakpoint.breakIpad`
        width: 270px;
    `}

    ${breakpoint.breakLarge`
        width: 350px;
    `}

    .wrapper {
        display: flex;
        flex-direction: column;
        padding: 12px 16px;
        gap: 10px;
        box-shadow: 0px -1px 4px rgba(0, 0, 0, 0.1);
        ${breakpoint.breakMobileMedium`
            flex-direction : row;
        `}
    }

    .totalPrice {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 6px;

        ${breakpoint.breakMobile`
            width: 200px;
            flex-direction : column;
            align-items: start;
        `}
        .text {
            font-size: 14px;
            color: #6e7375;
            font-weight: 500;
        }

        .value {
            font-size: 24px;
            font-weight: 500;
            font-family: Plus Jakarta Sans;
        }
    }
    .help-popup-overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.5);
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 5;
    }

    .help-popup-content {
        background: white;
        padding: 2rem;
        border-radius: ${theme.borderRadius.medium};
        max-width: 400px;
        ${breakpoint.breakMobile`
            width: 330px;   
        `}
        p {
            text-align: center;
            line-height: 150%;
            font-family: poppins;
        }
        h3 {
            margin-bottom: 1rem;
        }

        button {
            margin-top: 1rem;
            padding: 8px 16px;
        }
    }
`;

const CheckoutButtonStyled = styled(StyledButton)`
    margin-top: 0px;
    height: 54px;
    cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
`;

const SaveToQuoteButton = styled(StyledButton)`
    margin-top: 0px;
    height: 44px;
    background: transparent;
    border: 1px solid ${theme.colors.main};
    color: ${theme.colors.button_text1};
    font-size: 14px;
    cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};

    &:disabled {
        opacity: 0.5;
    }
`;

interface SaveToQuoteDropdownProps {
    onSaveNew: () => void;
    onAddToExisting?: () => void;
    isSaving?: boolean;
    isSubmitting?: boolean;
}

const SaveToQuoteDropdown = ({
    onSaveNew,
    onAddToExisting,
    isSaving,
    isSubmitting,
}: SaveToQuoteDropdownProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const disabled = isSubmitting || isSaving;

    if (!onAddToExisting) {
        return (
            <SaveToQuoteButton
                $borderRadius={theme.borderRadius.small}
                onClick={onSaveNew}
                disabled={disabled}
            >
                {isSaving ? (
                    <ClipLoaderCommon color={theme.colors.highlight} size={20} margin={0} />
                ) : (
                    "Save to Quote"
                )}
            </SaveToQuoteButton>
        );
    }

    return (
        <SaveToQuoteWrapper>
            <div className={`button-row ${disabled ? "disabled" : ""}`}>
                <div
                    className="main-label"
                    onClick={() => {
                        if (!disabled) onSaveNew();
                    }}
                >
                    {isSaving ? (
                        <ClipLoaderCommon color={theme.colors.highlight} size={20} margin={0} />
                    ) : (
                        "Save as new quote"
                    )}
                </div>
                <div className="divider" />
                <button
                    className={`chevron-toggle ${isOpen ? "open" : ""}`}
                    onClick={() => {
                        if (!disabled) setIsOpen((prev) => !prev);
                    }}
                    disabled={disabled}
                >
                    <img
                        src="/images/chevron-up.svg"
                        alt="chevron"
                        className={`chevron ${isOpen ? "rotated" : ""}`}
                    />
                </button>
            </div>
            {isOpen && !disabled && (
                <div className="dropdown-menu">
                    <div
                        className="dropdown-item"
                        onClick={() => {
                            setIsOpen(false);
                            onAddToExisting();
                        }}
                    >
                        Add to existing quote
                    </div>
                </div>
            )}
        </SaveToQuoteWrapper>
    );
};

const SaveToQuoteWrapper = styled.div`
    position: relative;
    width: 100%;

    .button-row {
        display: flex;
        align-items: center;
        height: 44px;
        border: 1px solid ${theme.colors.main};
        border-radius: ${theme.borderRadius.small};
        overflow: hidden;
        cursor: pointer;

        &.disabled {
            opacity: 0.5;
            cursor: not-allowed;
        }
    }

    .main-label {
        flex: 1;
        display: flex;
        align-items: center;
        justify-content: center;
        font-family: "Poppins", sans-serif;
        font-size: 14px;
        font-weight: 600;
        color: ${theme.colors.button_text1};
        height: 100%;
        padding: 0 12px;
    }

    .divider {
        width: 1px;
        height: 60%;
        background: ${theme.colors.main};
    }

    .chevron-toggle {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 40px;
        height: 100%;
        background: transparent;
        border: none;
        cursor: pointer;
        padding: 0;

        .chevron {
            width: 16px;
            height: 16px;
            transition: transform 0.2s;
        }

        .chevron.rotated {
            transform: rotate(180deg);
        }
    }

    .dropdown-menu {
        position: absolute;
        top: 100%;
        left: 0;
        right: 0;
        background: #fff;
        border: 1px solid #e6e9eb;
        border-radius: 6px;
        margin-top: 4px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        z-index: 10;
    }

    .dropdown-item {
        padding: 10px 14px;
        font-family: "Poppins", sans-serif;
        font-size: 13px;
        color: #131214;
        cursor: pointer;
        transition: background 0.15s;

        &:hover {
            background: #f4f6f7;
        }
    }
`;

const PriceBlockStyled = styled.div`
    font-family: Poppins;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    gap: 4px;

    .price-label-wrapper {
        display: flex;
        align-items: center;
        justify-content: flex-start;
        gap: 8px;

        .price-label {
            font-size: 0.875rem;
            color: ${theme.colors.muted};
            font-weight: 300;
        }

        .price-discount-badge-wrapper {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 4px;

            .price-discount-badge {
                font-weight: 500;
                font-size: 0.75rem;
                text-align: center;
                padding: 3px 8px;
                background-color: ${theme.colors.points};
                color: ${theme.colors.points_text};
                border-radius: ${theme.borderRadius.large};
                min-width: 60px;
            }

            .loading-spinner {
                width: 16px;
                height: 16px;
                border: 2px solid #f3f3f3;
                border-top: 2px solid ${theme.colors.highlight};
                border-radius: 50%;
                animation: spin 1s linear infinite;
            }

            @keyframes spin {
                0% {
                    transform: rotate(0deg);
                }
                100% {
                    transform: rotate(360deg);
                }
            }
        }
    }

    .price-value-wrapper {
        display: flex;
        align-items: center;
        justify-content: flex-start;
        gap: 8px;

        .price-discounted-value {
            font-size: 1.5rem;
            font-weight: 700;
            color: ${theme.colors.black};
        }

        /* A price the visitor *could* pay, not one that has been applied. The
           wording above carries the caveat — note this red is close to
           theme.colors.price (#E31E24), which marks the amount actually due. */
        .price-potential-value {
            font-size: 1.5rem;
            font-weight: 600;
            color: #e8252f;
        }

        .price-original-value {
            position: relative;
            font-size: 1rem;
            font-weight: 400;
            color: ${theme.colors.subtle};
            ${theme.colors.price
                ? `
                text-decoration: none;
                &::after {
                    content: '';
                    position: absolute;
                    left: -2px;
                    right: -2px;
                    top: 0;
                    bottom: 0;
                    background: linear-gradient(
                        to top left,
                        transparent calc(50% - 1px),
                        ${theme.colors.price} calc(50% - 1px),
                        ${theme.colors.price} calc(50% + 1px),
                        transparent calc(50% + 1px)
                    );
                }
            `
                : `text-decoration: line-through;`}
        }
    }
`;
const QuestionMarkHelper = styled.div`
    cursor: pointer;
`;

const PendingButtonsContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    width: 100%;
`;

const AcceptButton = styled(StyledButton)`
    margin-top: 0px;
    height: 54px;
    background: linear-gradient(
        to right,
        ${theme.gradients.button[0]},
        ${theme.gradients.button[1]}
    );
    color: ${theme.colors.button_text};
    cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};

    &:disabled {
        opacity: 0.6;
    }
`;

const DeclineButton = styled(StyledButton)`
    margin-top: 0px;
    height: 54px;
    background: #f5f5f5;
    color: #666;
    border: 1px solid #ddd;
    cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};

    &:hover:not(:disabled) {
        background: #e0e0e0;
    }

    &:disabled {
        opacity: 0.6;
    }
`;

const MobileCommissionLabel = styled.div`
    font-family: Poppins;
    font-size: 12px;
    line-height: 120%;
    font-weight: 500;
    color: ${theme.colors.commissionLabel};
    text-align: center;
    padding: 7px 0;
    background: #f0e9ff;
    display: block;

    ${breakpoint.breakTablet`
        display: none;
    `}
`;

const DesktopCommissionLabel = styled.div`
    font-family: Poppins;
    font-size: 12px;
    line-height: 120%;
    font-weight: 500;
    color: ${theme.colors.commissionLabel};
    text-align: center;
    padding: 8px 12px;
    background: #f0e9ff;
    border-radius: 0 0 ${theme.borderRadius.small} ${theme.borderRadius.small};
    display: none;

    ${breakpoint.breakTablet`
        display: block;
    `}
`;

const EarnPotentialBadge = styled.span`
    font-weight: 600;
    font-size: 0.75rem;
    padding: 3px 10px;
    margin-left: 8px;
    border-radius: ${theme.borderRadius.large};
    background-color: #fdeaeb;
    color: #e8252f;
    white-space: nowrap;
`;

const PotentialNote = styled.span`
    font-size: 11px;
    line-height: 1.3;
    color: ${theme.colors.gray_3};
    text-align: right;
`;
